'use memo';

import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useEffect, useState } from 'react';
import { z } from 'zod';

import ActionButton from '@/components/Button/ActionButton';
import { Checkbox } from '@/components/Checkbox';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { defaultNS, useTranslation } from '@/lib/i18n';
import {
  OAuth2ApiError,
  acceptConsentRequest,
  getConsentRequest,
  isSafeRedirectTo,
  mapConsentFlow,
  rejectConsentRequest,
  resolveConsentFlow,
  waitForMinLoaderVisible,
} from '@/lib/oauth2';
import type { ConsentFlowDecision, OAuth2ClientSummary } from '@/lib/oauth2';
import { navigateToUrl } from '@/utils/navigateToUrl';

import { OAuth2Card } from '../-components/OAuth2Card';
import { OAuth2ClientHeader } from '../-components/OAuth2ClientHeader';
import { OAuth2ErrorCard } from '../-components/OAuth2ErrorCard';
import { OAuth2ProgressScreen } from '../-components/OAuth2ProgressScreen';
import { OAuth2ScopeList } from '../-components/OAuth2ScopeList';
import { OAuth2Screen } from '../-components/OAuth2Screen';

const SearchSchema = z.object({
  consent_challenge: z.string().min(1).optional(),
});

export const Route = createFileRoute('/oauth2/consent/')({
  validateSearch: zodValidator(SearchSchema),
  component: OAuth2ConsentPage,
});

type RenderDecision = Extract<ConsentFlowDecision, { kind: 'render' }>;

/** auto-accept 可否の判定と Hydra accept は BE が実施。FE は resolveConsentFlow の action に従う。 */
function OAuth2ConsentPage() {
  const { consent_challenge } = Route.useSearch();
  const { t } = useTranslation(defaultNS, { keyPrefix: 'pages.oauth2' });
  const [renderDecision, setRenderDecision] = useState<RenderDecision | null>(
    null,
  );
  const [client, setClient] = useState<OAuth2ClientSummary | null>(null);
  const [asyncError, setAsyncError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);
  const [remember, setRemember] = useState(true);

  const missingChallengeError = !consent_challenge
    ? new Error('missing_consent_challenge')
    : null;
  const error = missingChallengeError ?? asyncError;
  const challenge = consent_challenge ?? '';

  const handleRedirect = (redirectTo: string) => {
    if (!isSafeRedirectTo(redirectTo)) {
      setAsyncError(new Error('unsafe_redirect_to'));
      return;
    }
    navigateToUrl(redirectTo, { replace: true });
  };

  useEffect(() => {
    if (!consent_challenge) return;
    let cancelled = false;
    void (async () => {
      const startedAt = performance.now();
      try {
        // 案1: resolve を先に呼び、待機中もアプリ名を表示する
        const branding = await getConsentRequest(consent_challenge);
        if (cancelled) return;
        setClient(branding.client);

        const decision = mapConsentFlow(
          await resolveConsentFlow(consent_challenge),
        );
        if (cancelled) return;

        if (decision.kind === 'redirect') {
          if (!isSafeRedirectTo(decision.redirectTo)) {
            setAsyncError(new Error('unsafe_redirect_to'));
            return;
          }
          await waitForMinLoaderVisible(startedAt);
          if (cancelled) return;
          navigateToUrl(decision.redirectTo, { replace: true });
          return;
        }
        setRenderDecision(decision);
      } catch (e) {
        if (cancelled) return;
        setAsyncError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [consent_challenge]);

  const submitAccept = async () => {
    if (!renderDecision) return;
    setSubmitting(true);
    try {
      const { redirectTo } = await acceptConsentRequest({
        challenge,
        grantScope: renderDecision.requestedScope,
        remember,
      });
      handleRedirect(redirectTo);
    } catch (e) {
      // 成功時は遷移するためローディングを維持し、失敗時のみ解除（二重送信防止）。
      setAsyncError(e);
      setSubmitting(false);
    }
  };

  const submitReject = async () => {
    setSubmitting(true);
    try {
      const { redirectTo } = await rejectConsentRequest({
        challenge,
        errorCode: 'access_denied',
      });
      handleRedirect(redirectTo);
    } catch (e) {
      setAsyncError(e);
      setSubmitting(false);
    }
  };

  if (error) {
    const fallbackKey =
      error instanceof OAuth2ApiError ? undefined : 'invalidRequest';
    return (
      <OAuth2Screen>
        <OAuth2ErrorCard error={error} fallbackKey={fallbackKey} />
      </OAuth2Screen>
    );
  }

  if (!renderDecision) {
    return (
      <OAuth2Screen>
        <OAuth2ProgressScreen client={client} />
      </OAuth2Screen>
    );
  }

  const clientName =
    renderDecision.client.clientName ?? renderDecision.client.clientId;

  return (
    <OAuth2Screen>
      <OAuth2Card
        header={
          <OAuth2ClientHeader
            client={renderDecision.client}
            title={t('consent.title', { clientName })}
            description={t('consent.description', { clientName })}
          />
        }
      >
        <VStack className="gap-6">
          <VStack className="gap-2">
            <Text variant="label" className="text-neutral-on-fill">
              {t('consent.scopesTitle')}
            </Text>
            <OAuth2ScopeList requestedScope={renderDecision.requestedScope} />
          </VStack>

          {renderDecision.requestedAudience.length > 0 && (
            <VStack className="gap-2">
              <Text variant="label" className="text-neutral-on-fill">
                {t('consent.audienceTitle')}
              </Text>
              <ul className="list-disc pl-5">
                {renderDecision.requestedAudience.map((audience) => (
                  <li key={audience}>
                    <Text variant="p" className="text-neutral-on-fill">
                      {audience}
                    </Text>
                  </li>
                ))}
              </ul>
            </VStack>
          )}

          <HStack className="items-center gap-2">
            <Checkbox
              id="oauth2-consent-remember"
              checked={remember}
              onCheckedChange={(checked) => setRemember(checked === true)}
            />
            <label htmlFor="oauth2-consent-remember" className="cursor-pointer">
              <Text variant="p" as="span" className="text-neutral-on-fill">
                {t('consent.rememberAlways')}
              </Text>
            </label>
          </HStack>

          <HStack className="justify-end gap-3">
            <ActionButton
              variant="outline"
              onClick={() => void submitReject()}
              disabled={submitting}
            >
              {t('consent.actions.deny')}
            </ActionButton>
            <ActionButton
              onClick={() => void submitAccept()}
              isLoading={submitting}
            >
              {t('consent.actions.allow')}
            </ActionButton>
          </HStack>
        </VStack>
      </OAuth2Card>
    </OAuth2Screen>
  );
}
